/**
 * CacheManager - Gestión de cache del navegador y service workers
 * 
 * Proporciona funciones para limpiar completamente la cache del navegador,
 * desregistrar service workers y forzar la recarga de la aplicación.
 */

/**
 * Elimina todas las caches del Service Worker
 * @returns {Promise<void>}
 */
export async function clearAllCaches() {
  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
      console.log('✓ Todas las caches eliminadas');
    } catch (error) {
      console.error('Error al eliminar caches:', error);
    }
  }
}

/**
 * Desregistra todos los service workers
 * @returns {Promise<void>}
 */
export async function unregisterServiceWorkers() {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
      console.log('✓ Service workers desregistrados');
    } catch (error) {
      console.error('Error al desregistrar service workers:', error);
    }
  }
}

/**
 * Limpia completamente la cache y recarga la aplicación
 * @param {boolean} forceReload - Si es true, fuerza la recarga sin cache
 * @returns {Promise<void>}
 */
export async function clearCacheAndReload(forceReload = true) {
  try {
    // Eliminar todas las caches
    await clearAllCaches();
    
    // Desregistrar service workers
    await unregisterServiceWorkers();
    
    console.log('✓ Cache limpiada completamente');
    
    // Forzar recarga
    if (forceReload) {
      // Método moderno: añadir timestamp a la URL para bypass de cache
      const url = window.location.href.split('?')[0] + '?_=' + Date.now();
      window.location.href = url;
    }
  } catch (error) {
    console.error('Error al limpiar cache:', error);
    // Intentar recarga de todas formas
    window.location.reload();
  }
}

/**
 * Limpia la cache sin recargar (útil para limpieza programada)
 * @returns {Promise<void>}
 */
export async function clearCacheOnly() {
  try {
    await clearAllCaches();
    await unregisterServiceWorkers();
    console.log('✓ Cache limpiada (sin recarga)');
  } catch (error) {
    console.error('Error al limpiar cache:', error);
  }
}

export default {
  clearAllCaches,
  unregisterServiceWorkers,
  clearCacheAndReload,
  clearCacheOnly
};
